import { CommonModule, DatePipe } from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpContext,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { NgModule, provideAppInitializer } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { NgbDropdownModule } from "@ng-bootstrap/ng-bootstrap";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import {
  AuthModule,
  LogLevel,
  StsConfigHttpLoader,
  StsConfigLoader,
} from "angular-auth-oidc-client";
import { AppComponent } from "app/app.component";
import { BYPASS_INTERCEPTOR } from "app/core/interceptor/auth-bypass.token";
import { LoginComponent } from "app/login/login.component";
import { normalizeOAuthRedirect } from "app/oauth-url-normalizer";
import { AlertsService } from "app/shared/alert/alert-service";
import { ConfirmService } from "app/shared/confirm/confirm.service";
import { MenuItems } from "app/shared/menu-items/menu-items";
import { SharedModule } from "app/shared/shared.module";
import { map } from "rxjs";
import { AppRoutingModule } from "./app-routing.module";
import { CanDeactivateGuard } from "./core/guards/candeactivateguard";
import { RouteCheck } from "./core/guards/route-check.service";
import { RequestInterceptorService } from "./core/interceptor/request.interceptor";
import { ProcessManagementService } from "./core/services/process-management.service";
import { HeaderComponent } from "./layout/components/header/header.component";
import { SidebarComponent } from "./layout/components/sidebar/sidebar.component";
import { LayoutComponent } from "./layout/layout.component";
import { InstallationManagementModule } from "./screens/installation-management/installation-management.module";
import { ProcessManagementModule } from "./screens/process-management/process-management.module";
import { SessionTimeoutComponent } from "./shared/SessionComponent/session-timeout.component";

/*HttpLoaderFactory used for navigation to correct language json template for internationalization feaure.*/
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

export const httpLoaderFactoryOidc = (httpClient: HttpClient) => {
  const config$ = httpClient
    .get<any>(`private/server/params`, {
      context: new HttpContext().set(BYPASS_INTERCEPTOR, true),
    })
    .pipe(
      map((customConfig: any) => {
        return {
          secureRoutes: [atob(customConfig["s1.oauth2.iss"])],
          authority: atob(customConfig["s1.oauth2.iss"]),
          redirectUrl: window.location.origin,
          postLogoutRedirectUri: window.location.origin,
          clientId: atob(customConfig["s1.oauth2.clientid"]),
          scope: atob(customConfig["s1.oauth2.scope"]),
          responseType: "code",
          silentRenew: true,
          maxIdTokenIatOffsetAllowedInSeconds: 600,
          issValidationOff: true,
          autoUserInfo: false,
          useRefreshToken: true,
          logLevel: LogLevel.None,
        };
      }),
    );
  return new StsConfigHttpLoader(config$);
};

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    LayoutComponent,
    SidebarComponent,
    HeaderComponent,
    SessionTimeoutComponent,
  ],
  bootstrap: [AppComponent],
  exports: [TranslateModule],
  imports: [
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    FormsModule,
    SharedModule,
    ReactiveFormsModule,
    NgbDropdownModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    ProcessManagementModule,
    InstallationManagementModule,
    AuthModule.forRoot({
      loader: {
        provide: StsConfigLoader,
        useFactory: httpLoaderFactoryOidc,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    MenuItems,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestInterceptorService,
      multi: true,
    },
    provideAppInitializer(normalizeOAuthRedirect),
    CanDeactivateGuard,
    RouteCheck,
    DatePipe,
    AlertsService,
    ConfirmService,
    ProcessManagementService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
